import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useFavorites } from "../lib/favorites";
import { apiClient, formatSAR } from "../lib/api";
import VerifiedBadge from "../components/VerifiedBadge";
import BetaOfferBanner from "../components/BetaOfferBanner";
import PrivateOffersCard from "../components/PrivateOffersCard";
import { FEATURES } from "../lib/features";
import {
  LayoutDashboard, LayoutGrid, ShieldCheck, CreditCard, Gavel, Wrench, MessageSquareWarning,
  Car, ArrowRight, Sparkles, MessageSquare, Heart, BookmarkPlus, TrendingUp,
  BarChart3, Crown, Star, Users, Zap, Award, Eye, Flame, Target, Phone,
  Settings, Rocket, Trophy, Gem, Building2, Receipt,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Tier definitions — aligned with /pricing plans:
//   basic       → Free (default)
//   plus        → Individual Plus (39 SAR/mo)
//   dealer_pro  → Dealer Pro (499 SAR/mo)
//   enterprise  → Custom-quote
// ─────────────────────────────────────────────────────────────────────
const TIERS = {
  basic: {
    id: "basic",
    label_ar: "أساسي",
    label_en: "Basic",
    price_sar: 0,
    accent: "#8a6f2e",
    accent_soft: "#c9a55c",
    next: "plus",
  },
  plus: {
    id: "plus",
    label_ar: "Plus للأفراد",
    label_en: "Individual Plus",
    price_sar: 39,
    accent: "#0ea5e9",
    accent_soft: "#7dd3fc",
    next: "dealer_pro",
  },
  dealer_pro: {
    id: "dealer_pro",
    label_ar: "Pro للتجار",
    label_en: "Dealer Pro",
    price_sar: 499,
    accent: "#c9a55c",
    accent_soft: "#e8c873",
    next: "enterprise",
  },
  enterprise: {
    id: "enterprise",
    label_ar: "Enterprise",
    label_en: "Enterprise",
    price_sar: null,
    accent: "#10b981",
    accent_soft: "#34d399",
    next: null,
  },
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const { count: favCount } = useFavorites();
  const [stats, setStats] = useState({ disputes: 0, listings: 0, conversations: 0, unread: 0, saved_searches: 0, views: 0, leads: 0 });
  const [kyc, setKyc] = useState(null);
  const [ownedBusinesses, setOwnedBusinesses] = useState([]);

  const tierId = user?.subscription_tier || "basic";
  const tier = TIERS[tierId] || TIERS.basic;

  useEffect(() => {
    if (!user) return;
    Promise.allSettled([
      apiClient.get("/disputes/mine"),
      apiClient.get("/conversations"),
      apiClient.get("/conversations/unread-count"),
      apiClient.get("/saved-searches"),
      apiClient.get("/kyc/me"),
      apiClient.get("/vehicles/mine").catch(() => null),
      apiClient.get("/platform/businesses/mine").catch(() => null),
    ]).then(([d, c, u, s, k, m, b]) => {
      const myListings = m?.value?.data?.items || [];
      const views = myListings.reduce((acc, x) => acc + (x.view_count || 0), 0);
      setStats({
        disputes: d.value?.data?.items?.length || 0,
        listings: myListings.length,
        conversations: c.value?.data?.items?.length || 0,
        unread: u.value?.data?.count || 0,
        saved_searches: s.value?.data?.items?.length || 0,
        views,
        // mock-derived: leads ~ favorites-on-my-listings (placeholder until backend exposes it)
        leads: Math.round(views * 0.045),
      });
      setKyc(k.value?.data || null);
      setOwnedBusinesses(b?.value?.data?.items || []);
    });
  }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/dashboard" }} replace />;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="dashboard-page">
      <DashboardHeader user={user} kyc={kyc} tier={tier} lang={lang} t={t} logout={logout} />

      {/* iter161 — Exclusive Beta Launch Offer (self-hiding component) */}
      <BetaOfferBanner />

      {/* iter179 — Super-Admin private offers (self-hiding component) */}
      <PrivateOffersCard />

      {/* Business-owner banner — surfaces the dedicated business dashboard
          for demo partners and any B2B account that owns a platform_business
          record. Non-owners never see this. */}
      {ownedBusinesses.length > 0 && (
        <BusinessOwnerBanner businesses={ownedBusinesses} lang={lang} />
      )}

      {/* Tier-aware stat ribbon */}
      <StatRibbon tier={tier} stats={stats} favCount={favCount} lang={lang} />

      {/* KYC banner — iter217: individual accounts only. Business AND
          member accounts must never see the beige individual-KYC card:
            • business accounts verify inside CARTAL Business (black
              "أكمل التوثيق" card in the business dashboard).
            • member accounts (users invited as employees of a business)
              have no personal marketplace activity, so individual KYC
              does not apply to them — showing this banner would push
              them into a track that is explicitly forbidden. */}
      {kyc && kyc.kyc_status !== "approved" && !((user?.account_type === "business") || (user?.account_type === "member") || (["dealer","agency","showroom","rental","financing","insurance","inspection","logistics","workshop","parts","fleet","corporate","exporter"].includes(user?.role)) || (user?.role === "business_member") || !!(user?.company_name || user?.business_name || user?.dealership_name)) && <KycBanner kyc={kyc} lang={lang} />}

      {/* Plus+ tier: analytics widget */}
      {tierId !== "basic" && <AnalyticsWidget tier={tier} stats={stats} lang={lang} />}

      {/* Dealer Pro / Enterprise: CRM leads board */}
      {(tierId === "dealer_pro" || tierId === "enterprise") && <CrmLeadsBoard tier={tier} lang={lang} />}

      {/* Tier-aware quick actions */}
      <QuickActionsGrid tier={tier} lang={lang} />

      {/* Tier-aware exclusive perks strip */}
      <ExclusivePerks tier={tier} lang={lang} />

      {/* Upgrade CTA — show only if not on top tier */}
      {tier.next && <UpgradeCallout currentTier={tier} nextTierId={tier.next} lang={lang} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 1. Header
// ─────────────────────────────────────────────────────────────────────
function DashboardHeader({ user, kyc, tier, lang, t, logout }) {
  const TierIcon = tier.id === "enterprise" ? Crown : tier.id === "dealer_pro" ? Trophy : tier.id === "plus" ? Star : Sparkles;
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap mb-7">
      <div className="flex-1 min-w-0">
        <div className="overline overline-gold mb-2 inline-flex items-center gap-2">
          <LayoutDashboard className="w-3 h-3" /> {t("nav.dashboard")}
        </div>
        <h1 className="font-display text-4xl tracking-tight font-semibold text-[#0a0a0a]">
          {lang === "ar" ? `مرحباً، ${user.name}` : `Welcome, ${user.name}`}
        </h1>
        <div className="text-[#525252] mt-2 text-sm inline-flex items-center gap-2 flex-wrap">
          <span className="font-medium">{user.email}</span>
          <span className="text-[#c9a55c]">·</span>
          <span className="text-[#8a6f2e] font-semibold">{t(`auth.roles.${user.role}`) || user.role}</span>
          {kyc?.kyc_status && <VerifiedBadge status={kyc.kyc_status} type={kyc.kyc_type} lang={lang} />}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {/* Tier badge */}
        <div
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-bold text-sm shadow-sm transition-transform hover:scale-[1.03]"
          style={{
            background: tier.id === "enterprise"
              ? "linear-gradient(135deg,#0a3d2c 0%,#10b981 60%,#34d399 100%)"
              : tier.id === "dealer_pro"
                ? "linear-gradient(135deg,#8a6f2e 0%,#c9a55c 60%,#e8c873 100%)"
                : tier.id === "plus"
                  ? "linear-gradient(135deg,#075985 0%,#0ea5e9 60%,#7dd3fc 100%)"
                  : "linear-gradient(135deg,#3a3a3a,#525252)",
            color: tier.id === "enterprise" ? "#fff" : tier.id === "dealer_pro" ? "#0a0a0a" : tier.id === "plus" ? "#fff" : "#f1ecdf",
            border: `1.5px solid ${tier.accent}`,
          }}
          data-testid={`dashboard-tier-badge-${tier.id}`}
        >
          <TierIcon className="w-4 h-4" />
          {lang === "ar" ? `باقة ${tier.label_ar}` : `${tier.label_en} plan`}
        </div>
        <button onClick={() => logout()} className="btn-ghost text-sm" data-testid="logout-button">
          {t("nav.logout")}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 2. Stat ribbon — tier-aware density
// ─────────────────────────────────────────────────────────────────────
function StatRibbon({ tier, stats, favCount, lang }) {
  const base = [
    { id: "fav", label: lang === "ar" ? "المفضلة" : "Favorites", value: favCount, icon: Heart, to: "/favorites" },
    { id: "msg", label: lang === "ar" ? "الرسائل" : "Messages", value: stats.conversations, sub: stats.unread > 0 ? `${stats.unread} ${lang === "ar" ? "جديد" : "new"}` : "", icon: MessageSquare, to: "/messages" },
    { id: "sav", label: lang === "ar" ? "بحوثاتي" : "Saved searches", value: stats.saved_searches, icon: BookmarkPlus, to: "/saved-searches" },
    { id: "dis", label: lang === "ar" ? "النزاعات" : "Disputes", value: stats.disputes, icon: MessageSquareWarning, to: "/disputes" },
  ];
  // Pro+ adds Listings and Total Views
  const pro = [
    { id: "lst", label: lang === "ar" ? "إعلاناتي" : "My listings", value: stats.listings, icon: Car, to: "/listings/manage" },
    { id: "vws", label: lang === "ar" ? "إجمالي المشاهدات" : "Total views", value: stats.views, icon: Eye, to: "/listings/manage" },
  ];
  // Premium adds Leads
  const premium = [
    { id: "lds", label: lang === "ar" ? "العملاء المحتملون" : "Leads", value: stats.leads, sub: lang === "ar" ? "آخر 30 يوم" : "Last 30 days", icon: Target, to: "/leads" },
  ];
  const items = (tier.id === "dealer_pro" || tier.id === "enterprise")
    ? [...base, ...pro, ...premium]
    : tier.id === "plus"
      ? [...base, ...pro]
      : base;

  const colCount = items.length > 4 ? "lg:grid-cols-7" : "lg:grid-cols-4";

  return (
    <div className={`grid sm:grid-cols-2 md:grid-cols-4 ${colCount} gap-3`} data-testid="dashboard-stats">
      {items.map((s) => <StatBox key={s.id} {...s} accent={tier.accent} />)}
    </div>
  );
}

function StatBox({ label, value, sub, icon: Icon, to, accent = "#c9a55c", testid }) {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}15`, border: `1px solid ${accent}55` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={2.2} />
        </div>
      </div>
      <div className="text-[10px] text-[#6b6b6b] uppercase tracking-widest mt-3 font-bold">{label}</div>
      <div className="font-display text-2xl mt-1 text-[#0a0a0a] font-semibold">{value}</div>
      {sub && <div className="text-[11px] mt-1 font-bold" style={{ color: accent }}>{sub}</div>}
    </>
  );
  const cls = "surface rounded-2xl p-4 hover:shadow-md transition-shadow block";
  if (to) return <Link to={to} className={cls} data-testid={`stat-${testid || label}`}>{inner}</Link>;
  return <div className={cls}>{inner}</div>;
}

// ─────────────────────────────────────────────────────────────────────
// 3. KYC banner
// ─────────────────────────────────────────────────────────────────────
function KycBanner({ kyc, lang }) {
  return (
    <Link to="/verify" className="block mt-6" data-testid="dashboard-kyc-banner">
      <div
        className="rounded-2xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow"
        style={{
          backgroundColor: kyc.kyc_status === "rejected" ? "#fef2f2" : "#fffaf0",
          border: `1px solid ${kyc.kyc_status === "rejected" ? "rgba(220,38,38,0.30)" : "rgba(201,165,92,0.40)"}`,
        }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{
            backgroundColor: kyc.kyc_status === "rejected" ? "rgba(220,38,38,0.10)" : "rgba(201,165,92,0.15)",
            color: kyc.kyc_status === "rejected" ? "#b91c1c" : "#8a6f2e",
          }}
        >
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-[#0a0a0a] inline-flex items-center gap-2 flex-wrap font-semibold">
            {kyc.kyc_status === "pending"
              ? (lang === "ar" ? "طلب التحقق قيد المراجعة" : "Verification in review")
              : kyc.kyc_status === "rejected"
                ? (lang === "ar" ? "طلب التحقق مرفوض — أعد الإرسال" : "Verification rejected — resubmit")
                : (lang === "ar" ? "وثّق حسابك — احصل على شارة \"موثّق\"" : "Verify your account — get a \"Verified\" badge")}
            <VerifiedBadge status={kyc.kyc_status} type={kyc.kyc_type} lang={lang} />
          </div>
          <p className="text-sm text-[#525252] mt-1">
            {kyc.kyc_status === "pending"
              ? (lang === "ar" ? "ستتم المراجعة خلال 24-48 ساعة." : "Review takes 24-48 hours.")
              : kyc.kyc_status === "rejected"
                ? kyc.submission?.review_notes || (lang === "ar" ? "اضغط لمراجعة الملاحظات وإعادة الإرسال." : "Tap to see notes and resubmit.")
                : (lang === "ar" ? "تحقق من الهوية الوطنية أو السجل التجاري لزيادة الثقة في إعلاناتك." : "Verify your National ID or Commercial Registration to boost trust.")}
          </p>
        </div>
        <ArrowRight className={`w-5 h-5 text-[#8a6f2e] shrink-0 mt-2 ${lang === "ar" ? "rotate-180" : ""}`} />
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 4. Analytics Widget (Pro+)
// ─────────────────────────────────────────────────────────────────────
function AnalyticsWidget({ tier, stats, lang }) {
  const isPremium = (tier.id === "dealer_pro" || tier.id === "enterprise");
  // Synthetic week-over-week mock — when backend exposes /analytics/me
  // for sellers, swap this in.
  const sparks = useMemo(() => Array.from({ length: 12 }, (_, i) => 30 + Math.round(Math.sin(i * 0.55) * 18 + i * 4)), []);
  const max = Math.max(...sparks);
  return (
    <div
      className="mt-8 rounded-3xl p-7 relative overflow-hidden"
      style={{
        background: isPremium
          ? "linear-gradient(135deg,#022c1a 0%,#0a3d2c 50%,#063e21 100%)"
          : tier.id === "plus"
            ? "linear-gradient(135deg,#082f49 0%,#0c4a6e 60%,#075985 100%)"
            : "linear-gradient(135deg,#0a0a0a 0%,#1f1a10 100%)",
        border: `2px solid ${tier.accent}`,
      }}
      data-testid="analytics-widget"
    >
      <div className="absolute -top-12 -end-12 w-48 h-48 rounded-full opacity-20 blur-2xl" style={{ background: tier.accent }} />
      <div className="relative">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div>
            <div className="overline mb-1" style={{ color: tier.accent_soft }}>
              {isPremium
                ? (lang === "ar" ? "تحليلات Pro للتجار" : "Dealer Pro analytics")
                : (lang === "ar" ? "تحليلات Plus" : "Plus analytics")}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold" style={{ color: "#f7eecd" }}>
              {lang === "ar" ? "أداء إعلاناتك خلال 12 أسبوع" : "Your listing performance — last 12 weeks"}
            </h2>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold" style={{ background: `${tier.accent}25`, color: tier.accent_soft, border: `1px solid ${tier.accent}55` }}>
            <TrendingUp className="w-3.5 h-3.5" /> +{Math.round((sparks[sparks.length - 1] - sparks[0]) / sparks[0] * 100)}% {lang === "ar" ? "نمو" : "growth"}
          </div>
        </div>

        <div className="grid md:grid-cols-[1fr_2fr] gap-6 items-center">
          {/* KPIs */}
          <div className="space-y-3">
            <MiniKpi label={lang === "ar" ? "إجمالي المشاهدات" : "Total views"} value={stats.views || 0} icon={Eye} accent={tier.accent_soft} />
            <MiniKpi label={lang === "ar" ? "نسبة التحويل" : "Conversion rate"} value={`${((stats.leads / Math.max(stats.views, 1)) * 100).toFixed(1)}%`} icon={Flame} accent={tier.accent_soft} />
            {isPremium && (
              <MiniKpi label={lang === "ar" ? "متوسط قيمة العميل" : "Avg lead value"} value={formatSAR(85000) + " SAR"} icon={Trophy} accent={tier.accent_soft} />
            )}
          </div>

          {/* Sparkline bars */}
          <div className="h-[140px] flex items-end gap-1.5">
            {sparks.map((v, i) => (
              <div
                key={i}
                className="flex-1 rounded-t transition-all hover:opacity-100"
                style={{
                  height: `${(v / max) * 100}%`,
                  background: `linear-gradient(180deg, ${tier.accent_soft}, ${tier.accent})`,
                  opacity: 0.55 + (i / sparks.length) * 0.45,
                  minHeight: 4,
                }}
                title={`Week ${i + 1}: ${v}`}
              />
            ))}
          </div>
        </div>

        {isPremium && (
          <div className="mt-6 pt-5 border-t border-white/10 grid sm:grid-cols-3 gap-3 text-sm" style={{ color: "rgba(247,238,205,0.85)" }}>
            <div className="flex items-center gap-2"><Award className="w-4 h-4" style={{ color: tier.accent_soft }} /> {lang === "ar" ? "تقارير ROI أسبوعياً" : "Weekly ROI reports"}</div>
            <div className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: tier.accent_soft }} /> {lang === "ar" ? "تحليل سلوك الزوار" : "Visitor behavior"}</div>
            <div className="flex items-center gap-2"><Rocket className="w-4 h-4" style={{ color: tier.accent_soft }} /> {lang === "ar" ? "توصيات بالذكاء الاصطناعي" : "AI recommendations"}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniKpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}25`, border: `1px solid ${accent}55` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "rgba(247,238,205,0.65)" }}>{label}</div>
        <div className="font-display text-xl font-semibold mt-0.5" style={{ color: "#f7eecd" }}>{value}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 5. CRM Leads board (Premium only)
// ─────────────────────────────────────────────────────────────────────
function CrmLeadsBoard({ tier, lang }) {
  // Demo placeholder leads — when /api/leads/mine is exposed, swap real data.
  const cols = [
    { id: "new", title: lang === "ar" ? "عملاء جدد" : "New leads", count: 6, color: "#3b82f6" },
    { id: "contacted", title: lang === "ar" ? "تم التواصل" : "Contacted", count: 4, color: "#f59e0b" },
    { id: "negotiating", title: lang === "ar" ? "قيد التفاوض" : "Negotiating", count: 2, color: "#a855f7" },
    { id: "closed", title: lang === "ar" ? "تم البيع" : "Closed", count: 1, color: "#10b981" },
  ];
  return (
    <div className="mt-8" data-testid="crm-leads-board">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline mb-1" style={{ color: tier.accent }}>
            {lang === "ar" ? "CRM — إدارة العملاء" : "CRM — Lead management"}
          </div>
          <h2 className="font-display text-2xl font-semibold text-[#0a0a0a]">
            {lang === "ar" ? "خط إنتاج المبيعات" : "Your sales pipeline"}
          </h2>
        </div>
        <Link to="/leads" className="text-sm font-bold inline-flex items-center gap-1" style={{ color: tier.accent }}>
          {lang === "ar" ? "إدارة كل العملاء" : "Manage all leads"} <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cols.map((c) => (
          <div
            key={c.id}
            className="rounded-2xl p-4 relative overflow-hidden bg-white hover:shadow-md transition-shadow"
            style={{ border: `1.5px solid ${c.color}55` }}
            data-testid={`crm-col-${c.id}`}
          >
            <div className="absolute top-0 start-0 end-0 h-1" style={{ background: c.color }} />
            <div className="flex items-center justify-between mt-1">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#525252]">{c.title}</div>
              <div className="font-display text-2xl font-bold" style={{ color: c.color }}>{c.count}</div>
            </div>
            <div className="mt-3 text-[11px] text-[#8a8a8a]">
              {lang === "ar" ? `${c.count} ${c.count === 1 ? "صفقة" : "صفقات"} نشطة` : `${c.count} active ${c.count === 1 ? "deal" : "deals"}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 6. Quick Actions
// ─────────────────────────────────────────────────────────────────────
function QuickActionsGrid({ tier, lang }) {
  const base = [
    { to: "/sell", icon: Car, ar: "بيع مركبتك", en: "List a vehicle", desc_ar: "ارفع إعلانك في دقائق", desc_en: "Post in minutes" },
    { to: "/my-listings", icon: LayoutGrid, ar: "إعلاناتي", en: "My listings", desc_ar: "تعديل، تعزيز، أو حذف", desc_en: "Edit, boost or delete" },
    { to: "/messages", icon: MessageSquare, ar: "صندوق الرسائل", en: "Inbox", desc_ar: "تواصل مع الجميع", desc_en: "Chat with everyone" },
    { to: "/payments/history", icon: Receipt, ar: "دفعاتي واشتراكاتي", en: "Payments & subscriptions", desc_ar: "الفواتير والحالة", desc_en: "Receipts & status" },
    { to: "/favorites", icon: Heart, ar: "المفضلة", en: "Saved", desc_ar: "ما حفظته", desc_en: "Your hearts" },
    ...(FEATURES.AUCTIONS_ENABLED ? [{ to: "/auctions", icon: Gavel, ar: "المزادات", en: "Auctions", desc_ar: "مزايدات مفتوحة", desc_en: "Live bidding" }] : []),
    { to: "/inspection", icon: Wrench, ar: "احجز فحصاً", en: "Inspection", desc_ar: "متنقل أو ورشة", desc_en: "Mobile or workshop" },
    { to: "/financing", icon: CreditCard, ar: "تمويل", en: "Financing", desc_ar: "قارن البنوك", desc_en: "Compare banks" },
    { to: "/faq", icon: MessageSquare, ar: "المساعدة", en: "Help", desc_ar: "أسئلة + مساعد AI", desc_en: "FAQ + AI" },
  ];
  const pro = [
    { to: "/listings/manage", icon: BarChart3, ar: "أداء إعلاناتي", en: "Listing analytics", desc_ar: "مشاهدات و تحويلات", desc_en: "Views & conversions", proOnly: true },
    { to: "/boost", icon: Zap, ar: "ميّز إعلانك", en: "Boost listing", desc_ar: "اظهر في القمة", desc_en: "Top of search", proOnly: true },
  ];
  const premium = [
    { to: "/leads", icon: Target, ar: "إدارة العملاء", en: "Lead manager", desc_ar: "CRM متكامل", desc_en: "Built-in CRM", premiumOnly: true },
    { to: "/profile/brand", icon: Gem, ar: "هويتك المخصّصة", en: "Custom branding", desc_ar: "صفحة بائع فاخرة", desc_en: "Luxury seller page", premiumOnly: true },
    { to: "/support/priority", icon: Phone, ar: "دعم أولوية", en: "Priority support", desc_ar: "تواصل مباشر", desc_en: "Direct hotline", premiumOnly: true },
  ];
  const items = (tier.id === "dealer_pro" || tier.id === "enterprise")
    ? [...base, ...pro, ...premium]
    : tier.id === "plus"
      ? [...base, ...pro]
      : base;

  return (
    <div className="mt-10">
      <div className="overline overline-gold mb-3">
        {lang === "ar" ? "إجراءات سريعة" : "Quick actions"}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => <ActionCard key={a.to} a={a} tier={tier} lang={lang} />)}
      </div>
    </div>
  );
}

function ActionCard({ a, tier, lang }) {
  const Icon = a.icon;
  const isElite = a.premiumOnly || a.proOnly;
  return (
    <Link
      to={a.to}
      className="group relative block rounded-2xl p-5 transition-all"
      style={{
        background: isElite
          ? `linear-gradient(135deg, #0a0a0a 0%, ${tier.id === "enterprise" || tier.id === "dealer_pro" ? "#022c1a" : "#1f1a10"} 100%)`
          : "linear-gradient(135deg,#0a0a0a 0%,#1a1a1a 100%)",
        border: `1px solid ${isElite ? tier.accent : "rgba(201,165,92,0.4)"}`,
      }}
      data-testid={`action-${a.to}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: `${tier.accent}25`, border: `1.5px solid ${tier.accent}` }}
        >
          <Icon className="w-5 h-5" style={{ color: tier.accent_soft }} strokeWidth={2.2} />
        </div>
        {a.premiumOnly && (
          <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ background: "linear-gradient(135deg,#c9a55c,#e8c873)", color: "#0a0a0a" }}>
            {lang === "ar" ? "Pro" : "Pro"}
          </span>
        )}
        {a.proOnly && !a.premiumOnly && (
          <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ background: "linear-gradient(135deg,#0ea5e9,#7dd3fc)", color: "#0a0a0a" }}>
            {lang === "ar" ? "Plus" : "Plus"}
          </span>
        )}
      </div>
      <div className="font-display text-lg font-bold leading-tight" style={{ color: "#f1ecdf" }}>
        {lang === "ar" ? a.ar : a.en}
      </div>
      <p className="text-sm mt-1 font-medium" style={{ color: tier.accent_soft }}>
        {lang === "ar" ? a.desc_ar : a.desc_en}
      </p>
      <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold" style={{ color: tier.accent_soft }}>
        {lang === "ar" ? "افتح" : "Open"}
        <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 7. Exclusive perks strip
// ─────────────────────────────────────────────────────────────────────
function ExclusivePerks({ tier, lang }) {
  const perks = {
    basic: [
      { ar: "نشر 5 إعلانات مجاناً", en: "5 free listings" },
      { ar: "بحث ذكي بالذكاء الاصطناعي", en: "AI smart search" },
      { ar: "ضمان كارتال — قريباً", en: "CARTAL Guarantee — Coming Soon" },
    ],
    plus: [
      { ar: "5 إعلانات مميّزة شهرياً", en: "5 featured listings per month" },
      { ar: "صور أكثر لكل إعلان (15 صورة)", en: "More photos per listing (15)" },
      { ar: "تحليلات أداء (Plus) لمدة 12 أسبوع", en: "Plus analytics (12-week trend)" },
      { ar: "شارة \"Plus\" زرقاء بجانب اسمك", en: "Blue Plus badge on your profile" },
      { ar: "دعم بأولوية عبر البريد", en: "Priority email support" },
    ],
    dealer_pro: [
      { ar: "إعلانات غير محدودة + رفع جماعي بالـ CSV", en: "Unlimited listings + bulk CSV upload" },
      { ar: "تحليلات تاجر متقدمة + توصيات بالذكاء الاصطناعي", en: "Advanced dealer analytics + AI suggestions" },
      { ar: "CRM متكامل لإدارة العملاء والصفقات", en: "Built-in CRM for leads & deals" },
      { ar: "صفحة معرض فاخرة بعلامتك التجارية", en: "Custom-branded showroom page" },
      { ar: "أولوية قصوى في نتائج البحث", en: "Top-tier search priority" },
      { ar: "وصول API + Webhooks", en: "API + Webhooks access" },
      { ar: "شارة \"Pro للتجار\" ذهبية حصرية", en: "Gold \"Dealer Pro\" badge" },
    ],
    enterprise: [
      { ar: "كل ميزات Pro + إدارة أسطول", en: "Everything in Pro + fleet management" },
      { ar: "تكاملات مخصصة (ERP / CRM / DMS)", en: "Custom integrations (ERP / CRM / DMS)" },
      { ar: "فريق حساب مخصّص + مدير علاقات", en: "Dedicated account team + AM" },
      { ar: "اتفاقيات مستوى خدمة (SLA) موقّعة", en: "Signed SLA agreements" },
      { ar: "توسّع متعدد الدول + دعم تشغيلي ميداني", en: "Multi-country rollout + field ops" },
      { ar: "دعم 24/7 — خط ساخن مباشر", en: "24/7 dedicated hotline support" },
    ],
  };
  const list = perks[tier.id] || perks.basic;
  return (
    <div className="mt-10 rounded-3xl p-6 sm:p-8" style={{ background: "linear-gradient(135deg,#faf8f1 0%,#fff 100%)", border: `1.5px solid ${tier.accent}33` }}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: tier.accent, color: tier.id === "enterprise" || tier.id === "plus" ? "#fff" : "#0a0a0a" }}>
          {tier.id === "enterprise" ? <Crown className="w-5 h-5" /> : tier.id === "dealer_pro" ? <Trophy className="w-5 h-5" /> : tier.id === "plus" ? <Star className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        </div>
        <div>
          <div className="overline" style={{ color: tier.accent }}>
            {lang === "ar" ? `مزايا باقتك` : `Your ${tier.label_en} perks`}
          </div>
          <h3 className="font-display text-xl font-semibold text-[#0a0a0a]">
            {lang === "ar" ? `كل ما تحصل عليه مع ${tier.label_ar}` : `Everything you get with ${tier.label_en}`}
          </h3>
        </div>
      </div>
      <ul className="grid sm:grid-cols-2 gap-2.5">
        {list.map((p, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-[#2a2a2a] font-medium">
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: tier.accent }} />
            <span>{lang === "ar" ? p.ar : p.en}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 8. Upgrade callout
// ─────────────────────────────────────────────────────────────────────
function UpgradeCallout({ currentTier, nextTierId, lang }) {
  const next = TIERS[nextTierId];
  const isEnterprise = next.id === "enterprise";
  const isDealer = next.id === "dealer_pro";
  return (
    <div
      className="mt-10 rounded-3xl p-7 relative overflow-hidden"
      style={{
        background: isEnterprise
          ? "linear-gradient(135deg,#022c1a 0%,#0a3d2c 50%,#063e21 100%)"
          : isDealer
            ? "linear-gradient(135deg,#1a1208 0%,#3d2e10 50%,#5a4615 100%)"
            : "linear-gradient(135deg,#082f49 0%,#0c4a6e 50%,#075985 100%)",
        border: `2px solid ${next.accent}`,
      }}
      data-testid="upgrade-callout"
    >
      <div className="absolute -bottom-12 -end-12 w-56 h-56 rounded-full opacity-25 blur-3xl" style={{ background: next.accent }} />
      <div className="relative flex flex-col lg:flex-row items-start gap-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: next.accent, color: isEnterprise || next.id === "plus" ? "#fff" : "#0a0a0a" }}>
          {isEnterprise ? <Crown className="w-7 h-7" /> : isDealer ? <Trophy className="w-7 h-7" /> : <Star className="w-7 h-7" />}
        </div>
        <div className="flex-1">
          <div className="overline mb-1" style={{ color: next.accent_soft }}>
            {lang === "ar" ? "ترقية الباقة" : "Upgrade your plan"}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold" style={{ color: "#f7eecd" }}>
            {lang === "ar"
              ? `انتقل من ${currentTier.label_ar} إلى ${next.label_ar} — افتح كل الميزات`
              : `Step up from ${currentTier.label_en} to ${next.label_en} — unlock everything`}
          </h2>
          <p className="mt-2 max-w-2xl font-medium" style={{ color: "rgba(247,238,205,0.78)" }}>
            {isEnterprise
              ? (lang === "ar"
                  ? "إدارة أسطول كاملة، تكاملات مخصصة، مدير حساب مخصص، اتفاقيات SLA، ودعم مخصّص 24/7 — كل ذلك بسعر مفصّل حسب احتياجك."
                  : "Full fleet management, custom integrations, dedicated account team, SLAs, and 24/7 dedicated support — pricing tailored to your needs.")
              : isDealer
                ? (lang === "ar"
                    ? "احصل على إعلانات غير محدودة، CRM متكامل، تحليلات بـ AI، صفحة معرض فاخرة، وصول API، وأولوية في البحث."
                    : "Unlock unlimited listings, built-in CRM, AI analytics, custom-branded showroom page, API access, and search priority.")
                : (lang === "ar"
                    ? "افتح 5 إعلانات مميزة شهرياً، تحليلات Plus، صور أكثر، ودعم بأولوية — بسعر مذهل."
                    : "Unlock 5 featured listings/month, Plus analytics, more photos, and priority support — at an amazing price.")}
          </p>
          {next.price_sar !== null ? (
            <div className="mt-3 inline-flex items-baseline gap-1.5 font-display" style={{ color: next.accent_soft }}>
              <span className="text-3xl font-bold">{next.price_sar}</span>
              <span className="text-sm font-medium">{lang === "ar" ? "ر.س / شهرياً" : "SAR / month"}</span>
            </div>
          ) : (
            <div className="mt-3 inline-flex items-baseline gap-1.5 font-display" style={{ color: next.accent_soft }}>
              <span className="text-2xl font-bold">{lang === "ar" ? "حسب الطلب" : "Custom quote"}</span>
            </div>
          )}
        </div>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm shadow-xl transition-transform hover:scale-105 shrink-0"
          style={{
            background: isEnterprise
              ? "linear-gradient(135deg,#10b981 0%,#34d399 60%,#6ee7b7 100%)"
              : isDealer
                ? "linear-gradient(135deg,#d4b56e 0%,#c9a55c 60%,#8a6f2e 100%)"
                : "linear-gradient(135deg,#0ea5e9 0%,#7dd3fc 60%,#bae6fd 100%)",
            color: isEnterprise ? "#0a3d2c" : isDealer ? "#0a0a0a" : "#0a3d4f",
          }}
          data-testid="upgrade-cta"
        >
          {lang === "ar" ? `قارن الباقات الآن` : `Compare plans now`}
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Business owner banner — CTA to jump into the dedicated business dashboard
// ─────────────────────────────────────────────────────────────────────
function BusinessOwnerBanner({ businesses, lang }) {
  const primary = businesses[0];
  return (
    <div
      className="mt-6 rounded-3xl overflow-hidden border-2 relative"
      style={{
        background: "linear-gradient(135deg,#0a0a0a 0%,#1a1611 100%)",
        borderColor: "#c9a55c",
      }}
      data-testid="business-owner-banner"
    >
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)" }}
          >
            <Building2 className="w-6 h-6" style={{ color: "#0a0a0a" }} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "#c9a55c" }}>
              {lang === "ar" ? "لوحة الشركة" : "Business dashboard"}
            </div>
            <div className="font-display text-lg sm:text-xl font-medium text-white truncate">
              {lang === "ar" ? (primary.name_ar || primary.name_en) : (primary.name_en || primary.name_ar)}
            </div>
            <div className="text-xs mt-1" style={{ color: "#e8e1cf" }}>
              {lang === "ar"
                ? "لديك حساب شركة — ادخل للوحة تحكم شركتك المخصّصة للتحقّق، المنتجات، الطلبات، والتحليلات."
                : "You own a business account — open your full B2B dashboard for verification, listings, requests and analytics."}
              {businesses.length > 1 && (
                <span className="ms-1" style={{ color: "#c9a55c" }}>
                  {lang === "ar" ? `(${businesses.length} شركات)` : `(${businesses.length} businesses)`}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {businesses.slice(0, 3).map((b) => (
            <Link
              key={b.id}
              to={`/business/${b.id}`}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors"
              style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
              data-testid={`business-dashboard-cta-${b.id}`}
            >
              {businesses.length > 1
                ? (lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar))
                : (lang === "ar" ? "دخول لوحة الشركة" : "Open business dashboard")}
              <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

